"use client";

import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServicesHero from "@/components/services/ServicesHero";
import VisaServices from "@/components/visaservices/VisaServices";

import CommonCTA from "@/components/CommonCTA";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  Briefcase,
  Building2,
  Calculator,
  Clock,
  Contact,
  CreditCard,
  FileCheck,
  Globe,
  Heart,
  MapPin,
  Navigation,
  Phone,
  Plane,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";

import WhyChooseUs from "@/components/WhyChooseUs";
import CorporateServices from "@/components/CorporateServices";
import OwnServices from "@/components/OwnServices";

/* ---------------- PROCESS DATA ---------------- */
const setupProcess = [
  {
    step: "01",
    title: "Consultation & Planning",
    description: "We analyze your business needs and recommend the optimal setup in the UAE.",
  },
  {
    step: "02",
    title: "Documentation",
    description: "Our experts prepare and submit all necessary legal documents for government approvals.",
  },
  {
    step: "03",
    title: "License Issuance",
    description: "Your company license is officially issued and registered with the relevant authorities.",
  },
  {
    step: "04",
    title: "Visa & Banking",
    description: "We handle investor/employee visas and facilitate business bank account openings.",
  },
];


export default function ServicesPage() {
  return (
    <main className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      {/* <ServicesHero /> */}
      <VisaServices />
      <div id="digital-marketing" />

      {/* 1. BUSINESS SETUP PROCESS (Back on Top) */}
      {/* <section className="py-20 bg-white">
        <div className="w-full mx-auto max-w-[1300px] xl:max-w-[1500px] 2xl:max-w-[1700px] px-6">
          <div className="text-center mb-16">
            <h2
              className="font-bold text-[#000000] leading-tight mb-4"
              style={{
                fontSize: "clamp(28px, 5vw, 48px)",
                fontWeight: "700",
                lineHeight: "1.3"
              }}
            >
              Business Setup <span className="text-[#B8893A]">Process</span>
            </h2>
            <p
              className="text-gray-500 max-w-2xl mx-auto"
              style={{
                fontSize: "clamp(14px, 1.2vw, 16px)",
                lineHeight: "1.6"
              }}
            >
              Our streamlined 4-step approach to getting your business live in the UAE.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {setupProcess.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative p-8 rounded-2xl bg-[#FAFAFA] border border-gray-100 hover:shadow-xl transition-all group"
              >
                <div className="text-5xl font-black text-[#B8893A]/10 absolute top-4 right-6 group-hover:text-[#B8893A]/20 transition-colors">
                  {p.step}
                </div>
                <h3
                  className="font-bold mb-4 text-[#0A0A0A] pr-10"
                  style={{
                    fontSize: "clamp(18px, 2vw, 20px)",
                    fontWeight: "600",
                    lineHeight: "1.3"
                  }}
                >
                  {p.title}
                </h3>
                <p
                  className="text-gray-600"
                  style={{
                    fontSize: "clamp(14px, 1.2vw, 16px)",
                    lineHeight: "1.6"
                  }}
                >
                  {p.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      <section className="grid gap-12 lg:grid-cols-2 items-center text-left md:text-center lg:text-left px-4 md:px-8">
          {/* Left */}
          <div>
            {/* Label pill */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E9DCC1] bg-[#fafafa] px-4 py-1.5 shadow-sm mb-6">
              <MapPin size={13} className="text-[#B8893A]" />
              <span className="text-xs uppercase tracking-[0.28em] text-[#B8893A] font-semibold">
                Support Services
              </span>
            </div>

            <h1 
              className="font-bold tracking-tight text-[#0A0A0A]"
              style={{ fontSize: "clamp(28px, 5vw, 48px)", fontWeight: "700", lineHeight: "1.3" }}
            >
              Global Visa &
              <span className="block text-[#B8893A]">Immigration Services</span>
            </h1>

            <h2 
              className="mt-4 font-bold text-[#3C3C3C]"
              style={{ fontSize: "clamp(18px, 2vw, 20px)", fontWeight: "600", lineHeight: "1.3" }}
            >
              Travel, Work & Explore the World with Confidence
            </h2>

            <p 
              className="mt-6 max-w-lg text-[#4A4A4A] md:mx-auto lg:mx-0"
              style={{ fontSize: "clamp(14px, 1.2vw, 16px)", lineHeight: "1.6" }}
            >
              Planning to travel or work abroad? AWS Businessman provides reliable and structured visa solutions for individuals and professionals seeking global opportunities. From short-term travel to long-term work permits, we guide you through every step with clarity, accuracy, and end-to-end support.
            </p>

            {/* Trust chips */}
            <div className="mt-6 flex flex-wrap gap-3 justify-start md:justify-center lg:justify-start">
              {[
                { icon: <Clock size={13} />, label: "24hr Processing" },
                { icon: <ShieldCheck size={13} />, label: "100% Compliant" },
                { label: "Expert Guidance" },
              ].map((chip) => (
                <div
                  key={chip.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E9DCC1] bg-[#fafafa] px-3.5 py-1.5 text-xs font-medium text-[#4A4A4A] shadow-sm"
                >
                  <span className="text-[#B8893A]">{chip.icon}</span>
                  {chip.label}
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-start md:justify-center lg:justify-start">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B8893A] px-7 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white shadow-[0_8px_28px_-6px_rgba(184,137,58,0.55)] transition hover:bg-[#D4A754] hover:shadow-[0_12px_36px_-6px_rgba(184,137,58,0.65)] active:scale-95"
              >
                Apply Now <ArrowRight size={15} />
              </Link>
              <a
                href="https://wa.me/971507987470"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E9DCC1] bg-[#fafafa] px-7 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#3C3C3C] shadow-sm transition hover:border-[#B8893A] hover:text-[#B8893A] active:scale-95"
              >
                <Phone size={14} /> Speak to an Expert
              </a>
            </div>
          </div>

          {/* Right — Glassy card stack */}
          <div className="relative">
            {/* Glow behind cards */}
            <div className="absolute inset-0 rounded-[36px] bg-[radial-gradient(ellipse_at_center,_rgba(184,137,58,0.12),_transparent_72%)] blur-2xl" />

            <div className="relative rounded-[32px] border border-[#E9DCC1] bg-white/80 backdrop-blur-xl p-7 shadow-[0_20px_80px_-24px_rgba(184,137,58,0.28)]">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#F7EFE0] text-[#B8893A] shadow-inner">
                  <Globe size={22} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.32em] text-[#B8893A] font-semibold">
                    Tourist Visa
                  </p>
                  <h2 className="mt-0.5 text-lg font-bold text-[#0A0A0A]">
                    UAE Visitor Processing
                  </h2>
                </div>
              </div>

              {/* Glassy feature rows */}
              <div className="space-y-3">
                {[
                  {
                    icon: <Clock size={16} />,
                    label: "Fast review",
                    text: "Complete visa preparation within 24 hours so clients travel on schedule.",
                    bg: "bg-[#FEF8ED]/80",
                  },
                  {
                    icon: <FileCheck size={16} />,
                    label: "Document support",
                    text: "Professional document checks, permits and travel approvals with local sponsor coordination.",
                    bg: "bg-white/60",
                  },
                  {
                    label: "Clear updates",
                    text: "Transparent status tracking from application to arrival.",
                    bg: "bg-[#FEF8ED]/80",
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    className={`flex items-start gap-4 rounded-2xl border border-[#EDE0C7] ${row.bg} backdrop-blur-sm p-4 transition hover:-translate-y-0.5 hover:shadow-[0_6px_20px_-10px_rgba(184,137,58,0.3)]`}
                  >
                    <div className="shrink-0 h-8 w-8 rounded-xl bg-[#fafafa] grid place-items-center shadow-sm text-[#B8893A]">
                      {row.icon}
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#A07A3F] font-bold">
                        {row.label}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-[#4A4A4A]">{row.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link href="/services" className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#B8893A] cursor-pointer group">
                Learn more{" "}
                <ArrowRight size={14} className="transition group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>


          <div className="mt-20 flex items-center gap-4">
          <div className="flex-1 h-px bg-[#E9DCC1]" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#B8893A] font-semibold">
            Our Visa & Immigration Services
          </span>
          <div className="flex-1 h-px bg-[#E9DCC1]" />
        </div>

        {/* ─── 3 FEATURE CARDS ─── */}
        <section className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 px-4 sm:px-6 lg:px-8">

          {[
            {
              icon: <Globe size={20} />,
              title: "UAE & Gulf Visit Visas",
              text: "Quick and reliable processing for UAE and Gulf region visit visas, including tourist and short-term travel visas. We ensure smooth documentation and fast application support.",
              stat: "Fast",
              statLabel: "Application",
            },
            {
              icon: <FileCheck size={20} />,
              title: "Schengen Visa Services",
              text: "Professional support for Schengen visa applications with full documentation guidance and submission assistance for Europe travel.",
              stat: "Full",
              statLabel: "Support",
            },
            {
              icon: <Sparkles size={20} />,
              title: "Europe Visit Visas",
              text: "Complete assistance for Europe visit visas, helping you plan smooth travel across multiple European destinations.",
              stat: "Smooth",
              statLabel: "Planning",
            },
            {
              icon: <Users size={20} />,
              title: "Europe Work Permits",
              text: "End-to-end support for Europe work permits, including documentation guidance, application processing, and employer coordination assistance.",
              stat: "Complete",
              statLabel: "Permits",
            },
          ].map((card) => (
            <div
              key={card.title}
              className="group rounded-3xl border border-[#E9DCC1] bg-[#fafafa] p-7 px-2 shadow-[0_8px_40px_-20px_rgba(184,137,58,0.18)] hover:-translate-y-1 transition-all duration-300"
            >

              {/* Top row */}
              <div className="flex items-start justify-between mb-6">

                <div className="h-11 w-11 rounded-2xl grid place-items-center bg-[#FEF8ED] text-[#B8893A]">
                  {card.icon}
                </div>

                <div className="text-right">
                  <p className="text-sm font-black text-[#B8893A] uppercase tracking-tighter">
                    {card.stat}
                  </p>
                  <p className="text-[9px] uppercase tracking-widest text-[#9A9A9A]">
                    {card.statLabel}
                  </p>
                </div>

              </div>

              {/* Title */}
              <h3 
                className="font-bold text-[#0A0A0A]"
                style={{ fontSize: "clamp(16px, 1.8vw, 18px)", fontWeight: "600" }}
              >
                {card.title}
              </h3>

              {/* Text */}
              <p 
                className="mt-3 text-[#4A4A4A]"
                style={{ fontSize: "clamp(13px, 1.1vw, 15px)", lineHeight: "1.6" }}
              >
                {card.text}
              </p>

           

            </div>
          ))}
        </section>

        {/* ─── GLASSY PROCESS STRIP ─── */}
        <section className="mt-16 rounded-[28px] border border-[#E9DCC1] bg-white/70 backdrop-blur-xl p-7 sm:p-10 shadow-[0_8px_48px_-20px_rgba(184,137,58,0.18)] ">
          <div className="text-left md:text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8893A] font-semibold">
              Why Choose AWS Businessman
            </p>
            <h2 
              className="mt-2 font-bold"
              style={{ fontSize: "clamp(20px, 2.5vw, 24px)", fontWeight: "700", lineHeight: "1.3" }}
            >
              Simplifying complex visa processes
            </h2>
          </div>

          <div className="grid gap-10 grid-cols-1 md:grid-cols-5">
            {[
              {
                step: "01",
                title: "Fast Processing",
                text: "Quick and reliable processing for all visa categories with minimal turnaround time.",
              },
              {
                step: "02",
                title: "Documentation",
                text: "Step-by-step support to ensure your application meets all regulatory requirements.",
              },
              {
                step: "03",
                title: "Expert Guidance",
                text: "Experienced consultants providing clarity and accuracy for global opportunities.",
              },
              {
                step: "04",
                title: "Transparent",
                text: "Stress-free process with clear communication at every stage of your application.",
              },
              {
                step: "05",
                title: "Personalized",
                text: "Dedicated client assistance tailored to your specific travel and work goals.",
              },
            ].map((s, i) => (
              <div key={s.step} className="relative flex flex-col items-start">
                <div className="shrink-0 h-10 w-10 rounded-full border-2 border-[#B8893A] bg-[#FEF8ED] grid place-items-center text-xs font-black text-[#B8893A] z-10 mb-3">
                  ✓
                </div>
                <div>
                  <h3 className="font-bold text-[#0A0A0A] text-sm">{s.title}</h3>
                  <p className="mt-1.5 text-xs leading-5 text-[#5B5B5B]">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

      {/* <OwnServices /> */}
      <CommonCTA
        title="Let us handle your visa process while you focus on your plans ahead."
        description="Contact us today and take the first step toward your destination."
        primaryText="Get Started Now"
      />

      <Footer />
    </main>
  );
}

